import React from 'react';
import { Typography, makeStyles } from '@material-ui/core';
import { ArrowForward } from '@material-ui/icons';
import { BRAND } from 'theme';
import { HomeModule } from '../types';
import { ARROW_NUDGE, lightCard, nudgeTransition } from './cardStyles';
import ModuleLink from './ModuleLink';
import NewBadge from './NewBadge';

interface IProps {
  module: HomeModule;
}

const useStyles = makeStyles((theme) => ({
  card: {
    ...lightCard(theme),
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    minHeight: 150,
    padding: theme.spacing(2.5, 3),
    '&:hover $arrow': ARROW_NUDGE,
    '&:hover $illustration': {
      opacity: 0.4,
    },
  },
  // Текст не заходит в правую часть карточки, где лежат стрелка и иллюстрация.
  text: {
    position: 'relative',
    zIndex: 1,
    maxWidth: 'calc(100% - 128px)',
  },
  title: {
    fontWeight: 600,
    lineHeight: 1.25,
  },
  subtitle: {
    marginTop: theme.spacing(0.5),
  },
  // Иллюстрация — фон карточки: крупная, полупрозрачная, уходит за правый и нижний края; текст поверх неё.
  illustration: {
    position: 'absolute',
    right: -theme.spacing(2),
    bottom: -theme.spacing(1.5),
    width: 160,
    height: 120,
    opacity: 0.2,
    pointerEvents: 'none',
    transition: theme.transitions.create('opacity', { duration: theme.transitions.duration.short }),
    '& svg': {
      width: '100%',
      height: '100%',
    },
  },
  logo: {
    right: theme.spacing(3),
    bottom: theme.spacing(2.5),
    width: 72,
    height: 72,
    opacity: 1,
  },
  arrow: {
    ...nudgeTransition(theme),
    position: 'absolute',
    top: theme.spacing(2.5),
    right: theme.spacing(2.5),
    color: BRAND.accent,
  },
  // Слева от стрелки (иконка 24px) и по её центру по вертикали.
  newBadge: {
    position: 'absolute',
    zIndex: 1,
    top: theme.spacing(2.5) + 12,
    right: theme.spacing(2.5) + 24 + theme.spacing(1),
    transform: 'translateY(-50%)',
  },
}));

/** Карточка модуля главной: название и подпись, стрелка в углу, иллюстрация фоном; куда ведёт — решает цель модуля. */
const ModuleCard = ({ module }: IProps) => {
  const classes = useStyles();
  const Illustration = module.illustration;

  return (
    <ModuleLink target={module} className={classes.card}>
      <span className={classes.text}>
        <Typography component="span" variant="h6" display="block" className={classes.title}>
          {module.title}
        </Typography>

        <Typography component="span" variant="body2" display="block" color="textSecondary" className={classes.subtitle}>
          {module.subtitle}
        </Typography>
      </span>

      <span
        className={module.isLogo ? `${classes.illustration} ${classes.logo}` : classes.illustration}
        aria-hidden="true"
      >
        <Illustration />
      </span>

      {module.isNew && <NewBadge className={classes.newBadge} />}
      <ArrowForward className={classes.arrow} />
    </ModuleLink>
  );
};

export default ModuleCard;
